import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import AddressAutocomplete from '@atoms/AddressAutocomplete';
import Asterisk from '@atoms/Asterisk/index';
import CheckboxWithoutForm from '@atoms/CheckboxWithoutForm';
import TextInput from '@atoms/TextInput/index';
import SelectDay from '@molecules/SelectDay';
import SelectMonth from '@molecules/SelectMonth';
import SelectYear from '@molecules/SelectYear';
import SelectCountries from '@molecules/SelectCountries';
import SelectStatesEcomm from '@molecules/SelectStatesEcomm';
import { validateMinimumAge } from './index.helpers';
import { BodySmall, LabelLarge } from '@styled';
import { useScreenSmDown } from '@hooks/useResponsive';

const StyledCheckboxContainer = styled.div`
    margin-top: -8px;
    margin-bottom: 1.5rem;
`;

const StyledContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
`;

const StyledColumn = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 0.5rem;
    width: 100%;

    &.one-per-row {
        flex: 0 0 100%;
    }

    &.two-per-row {
        flex: 0 0 calc(50% - 16px);
        max-width: 333px;
    }

    &.three-per-row {
        flex: 0 0 calc(33.33333% - 25px);
        max-width: 211px;
    }
`;

function hasAddressError(errors, touched, values) {
    if (values?.mainAddressFull == null || values?.mainAddressFull === '') return false;
    if (!errors || !touched) return false;
    const fields = [
        'mainAddressAddress1',
        'mainAddressCity',
        'mainAddressState',
        'mainAddressCountry',
        'mainAddressZip',
    ];

    return fields.some(field => {
        return field in errors && field in touched;
    });
}

const AccountContactInformationFields = ({
    ageValidationError,
    isPrimaryEmailRequired,
    hasBirthdate,
    setIsManualContactAddress,
    setAgeValidationError,
    disableName = false,
    isViewer,
}) => {
    const { errors, isSubmitting, isValidating, setFieldValue, touched, values } = useFormikContext();
    const [showExpandedAddressFields, setShowExpandedAddressFields] = useState(false);
    const isMobile = useScreenSmDown();

    useEffect(() => {
        if (hasAddressError(errors, touched, values)) {
            setShowExpandedAddressFields(true);
        }
    }, [errors, touched, values]);

    useEffect(() => {
        if (isPrimaryEmailRequired) {
            setFieldValue('isPrimaryEmailRequired', true);
        } else {
            setFieldValue('isPrimaryEmailRequired', false);
        }
    }, [isPrimaryEmailRequired, setFieldValue]);

    useEffect(() => {
        setIsManualContactAddress(showExpandedAddressFields);
    }, [setIsManualContactAddress, showExpandedAddressFields]);

    // Validate age on form submission
    useEffect(() => {
        if (!isSubmitting || !values.birthdateDay || !values.birthdateMonth || !values.birthdateYear) return;

        const isValidAge = validateMinimumAge({
            birthdateDay: values.birthdateDay,
            birthdateMonth: values.birthdateMonth,
            birthdateYear: values.birthdateYear,
        });

        if (!isValidAge) {
            setAgeValidationError('You must be over the age of 16 to become an ICSC Member');
            return;
        }

        setAgeValidationError('');
    }, [isSubmitting, isValidating, setAgeValidationError, values]);

    const hasBirthdateText = (
        <BodySmall as="span">
            Please email{' '}
            <a className="accent" href="mailto:membership@icsc.com">
                membership@icsc.com
            </a>{' '}
            to update {isViewer ? 'your' : 'their'} birthday.
        </BodySmall>
    );

    return (
        <StyledContainer>
            <StyledColumn className="two-per-row">
                <TextInput
                    disabled={disableName}
                    label="First Name"
                    name="firstName"
                    required={!disableName}
                    type="text"
                />
            </StyledColumn>

            <StyledColumn className="two-per-row">
                <TextInput
                    disabled={disableName}
                    label="Last Name"
                    name="lastName"
                    required={!disableName}
                    type="text"
                />
            </StyledColumn>

            <StyledColumn className="one-per-row">
                <AddressAutocomplete
                    id="mainAddressFull"
                    label="Address"
                    name="mainAddressFull"
                    placeholder="Start typing an address..."
                    required
                    onAddressSelect={address => {
                        setFieldValue('mainAddressAddress1', address.address1);
                        setFieldValue('mainAddressAddress2', address.address2 || '');
                        setFieldValue('mainAddressAddress3', address.address3 || '');
                        setFieldValue('mainAddressCity', address.city);
                        setFieldValue('mainAddressState', address.state);
                        setFieldValue('mainAddressCountry', address.country);
                        setFieldValue('mainAddressZip', address.zip);
                    }}
                    onAddressClear={() => {
                        setFieldValue('mainAddressAddress1', '');
                        setFieldValue('mainAddressAddress2', '');
                        setFieldValue('mainAddressAddress3', '');
                        setFieldValue('mainAddressCity', '');
                        setFieldValue('mainAddressState', '');
                        setFieldValue('mainAddressCountry', '');
                        setFieldValue('mainAddressZip', '');
                    }}
                    onShowExpandedAddressFields={value => {
                        setShowExpandedAddressFields(value);
                    }}
                    showEditAddress
                />
            </StyledColumn>
            {showExpandedAddressFields && (
                <>
                    <StyledColumn className="one-per-row">
                        <SelectCountries
                            id="mainAddressCountry"
                            name="mainAddressCountry"
                            label="Select Your Country"
                            required
                        />
                    </StyledColumn>
                    <StyledColumn className="one-per-row">
                        <TextInput
                            id="mainAddressAddress1"
                            label="Address Line 1"
                            name="mainAddressAddress1"
                            type="text"
                            required
                        />
                    </StyledColumn>
                    <StyledColumn className="one-per-row">
                        <TextInput
                            id="mainAddressAddress2"
                            label="Address Line 2"
                            name="mainAddressAddress2"
                            type="text"
                        />
                    </StyledColumn>
                    <StyledColumn className="one-per-row">
                        <TextInput
                            id="mainAddressAddress3"
                            label="Address Line 3"
                            name="mainAddressAddress3"
                            type="text"
                        />
                    </StyledColumn>
                    <StyledColumn className="one-per-row">
                        <TextInput id="mainAddressCity" label="City" name="mainAddressCity" type="text" required />
                    </StyledColumn>
                    <StyledColumn className="one-per-row">
                        <TextInput
                            id="mainAddressZip"
                            label="Zip/Postal Code"
                            name="mainAddressZip"
                            type="text"
                            required
                        />
                    </StyledColumn>
                    <StyledColumn className="one-per-row">
                        <SelectStatesEcomm
                            id="mainAddressState"
                            name="mainAddressState"
                            countryFieldName="mainAddressCountry"
                            required
                        />
                    </StyledColumn>
                </>
            )}

            <StyledColumn className="two-per-row mb-0">
                <TextInput label="Work Phone Number" name="workPhone" required type="text" />
            </StyledColumn>

            <StyledColumn className="two-per-row mb-0">
                <TextInput label="Mobile Phone Number" name="cellPhone" required type="text" />
            </StyledColumn>

            <StyledCheckboxContainer>
                <CheckboxWithoutForm
                    checked={values.smsUpdates}
                    onCheckChange={checked => setFieldValue('smsUpdates', checked)}
                >
                    Send me SMS updates
                </CheckboxWithoutForm>
            </StyledCheckboxContainer>

            {isPrimaryEmailRequired && (
                <StyledColumn className="one-per-row">
                    <TextInput label="Primary Email" name="email" type="email" required />
                </StyledColumn>
            )}

            <StyledColumn className="one-per-row">
                <TextInput label="Secondary Email" name="secondaryEmail" type="email" />
            </StyledColumn>

            <StyledColumn className="mb-0.5rem one-per-row">
                <LabelLarge>
                    Date of Birth <Asterisk />
                </LabelLarge>
                {ageValidationError ? (
                    <BodySmall className="age-validation-error mt-0.25rem text-error">{ageValidationError}</BodySmall>
                ) : null}
            </StyledColumn>

            {hasBirthdate ? <StyledColumn className="one-per-row">{hasBirthdateText}</StyledColumn> : null}

            <StyledColumn className="three-per-row">
                <SelectMonth
                    displayAsNumber
                    isDisabled={hasBirthdate}
                    hideLabel
                    id="birthdateMonth"
                    name="birthdateMonth"
                    placeholder={isMobile ? 'MM' : '-- Month --'}
                    label="Month"
                    required
                />
            </StyledColumn>

            <StyledColumn className="three-per-row">
                <SelectDay
                    isDisabled={hasBirthdate}
                    hideLabel
                    id="birthdateDay"
                    name="birthdateDay"
                    label="Day"
                    placeholder={isMobile ? 'DD' : '-- Day --'}
                    required
                />
            </StyledColumn>

            <StyledColumn className="three-per-row">
                <SelectYear
                    isDisabled={hasBirthdate}
                    hideLabel
                    id="birthdateYear"
                    name="birthdateYear"
                    label="Year"
                    placeholder={isMobile ? 'YYYY' : '-- Year --'}
                    required
                />
            </StyledColumn>
        </StyledContainer>
    );
};

AccountContactInformationFields.propTypes = {
    ageValidationError: PropTypes.string,
    isPrimaryEmailRequired: PropTypes.bool,
    hasBirthdate: PropTypes.bool,
    setAgeValidationError: PropTypes.func,
    setIsManualContactAddress: PropTypes.func,
    disableName: PropTypes.bool,
    isViewer: PropTypes.bool,
};

export default AccountContactInformationFields;
